import { useState, useEffect, useMemo } from "react";
import {
  Form,
  Button,
  Modal,
  Pagination,
  Spinner,
  Tabs,
  Tab,
  Table,
  Badge,
  Row,
  Col,
} from "react-bootstrap";
import { useFindUsers } from "../../api/queries/useFindUser";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import { BsTrash } from "react-icons/bs";
import { useDeleteUser } from "../../api/mutations/useDeleteUser";
import { User } from "../../types/Types";
import EditModal from "../Account/EditModal";
import { FiUsers } from "react-icons/fi";
import AdminPanelHeader from "./AdminPanelHeader";
import { formatCityCountryLabel } from "../../utils/cityAsyncSelectOptions";
import { matchesAdminUserSearch } from "../../utils/adminUserSearch";
import { resolveProfessionalPhotoUrl } from "../../utils/profilePhoto";
import {
  EARLY_STAGE_MEMBERSHIP,
  isYouthOrEarlyStageMembership,
  LEGACY_YOUTH_MEMBERSHIP,
} from "../../utils/membershipTier";

const FILTER_FETCH_SIZE = 500;
/** Wide fetch for admin name search (exact + accent-insensitive) on the client. */
const SEARCH_FETCH_SIZE = 5000;

const STRIPE_STATUS_FILTERS = [
  { value: "active", label: "Active" },
  { value: "trialing", label: "Trialing" },
  { value: "past_due", label: "Past due" },
  { value: "unpaid", label: "Unpaid" },
  { value: "incomplete", label: "Incomplete" },
  { value: "incomplete_expired", label: "Incomplete expired" },
  { value: "paused", label: "Paused" },
  { value: "canceled", label: "Canceled" },
  { value: "none", label: "No Stripe status" },
] as const;

function normalizeStripeStatus(status?: string | null): string {
  return (status ?? "").trim().toLowerCase();
}

function matchesStripeStatusFilter(
  status: string | undefined,
  filter: string,
): boolean {
  const wanted = filter.trim().toLowerCase();
  if (!wanted) return true;
  const current = normalizeStripeStatus(status);
  if (wanted === "none" || wanted === "empty") {
    return !current;
  }
  if (wanted === "canceled" || wanted === "cancelled") {
    return current === "canceled" || current === "cancelled";
  }
  return current === wanted;
}

function stripeStatusBadgeTone(
  status: string,
): "success" | "warning" | "danger" | "secondary" {
  const s = normalizeStripeStatus(status);
  if (s === "active" || s === "trialing") return "success";
  if (s === "past_due" || s === "unpaid" || s === "incomplete" || s === "paused") {
    return "warning";
  }
  if (
    s === "canceled" ||
    s === "cancelled" ||
    s === "incomplete_expired"
  ) {
    return "danger";
  }
  return "secondary";
}

function matchesMembershipFilter(
  membershipName: string | undefined,
  filter: string,
): boolean {
  const selected = filter.trim();
  if (!selected) return true;
  const name = (membershipName ?? "").trim();
  if (!name) return false;
  if (name.toLowerCase() === selected.toLowerCase()) return true;
  // Treat Youth as Early Stage for admin filtering.
  if (
    selected === EARLY_STAGE_MEMBERSHIP ||
    selected === LEGACY_YOUTH_MEMBERSHIP
  ) {
    return isYouthOrEarlyStageMembership(name);
  }
  return false;
}

const ManageUsers = () => {
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [userData, setUserData] = useState<User | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [userType, setUserType] = useState<"all" | "members" | "explorer">(
    () => {
      const requested = (
        location.state as {
          usersSubTab?: "all" | "members" | "explorer" | "nonusers";
        } | null
      )?.usersSubTab;
      if (
        requested === "members" ||
        requested === "explorer" ||
        requested === "all"
      ) {
        return requested;
      }
      return "all";
    },
  );
  const [membershipFilter, setMembershipFilter] = useState("");
  const [stripeStatusFilter, setStripeStatusFilter] = useState("");
  const pageSize = 50;
  const showMemberFilters = userType === "all" || userType === "members";
  const { data: memberships = [] } = useGetMemberships();

  const membershipOptions = useMemo(() => {
    const names = memberships
      .map((m) => (m.name || "").trim())
      .filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [memberships]);

  useEffect(() => {
    const requestedType = (
      location.state as {
        usersSubTab?: "all" | "members" | "explorer" | "nonusers";
      } | null
    )?.usersSubTab;
    if (
      requestedType === "members" ||
      requestedType === "explorer" ||
      requestedType === "all"
    ) {
      setUserType(requestedType);
    } else if (requestedType === "nonusers") {
      setUserType("all");
    }
  }, [location.state]);

  useEffect(() => {
    if (userType === "explorer") {
      setStripeStatusFilter("");
      setMembershipFilter("");
    }
  }, [userType]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, userType, membershipFilter, stripeStatusFilter]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const effectiveStripeFilter = showMemberFilters ? stripeStatusFilter : "";
  const effectiveMembershipFilter = showMemberFilters ? membershipFilter : "";
  const searchTrimmed = debouncedSearchTerm.trim();
  const useClientSideSearch = searchTrimmed.length > 0;
  // Client-side filters need a wider fetch because staging API may not support them yet.
  // Name search also runs client-side so accented names match (exact + broad).
  const useClientSideFilters =
    !!effectiveMembershipFilter ||
    !!effectiveStripeFilter ||
    useClientSideSearch;

  const {
    data: userResponse,
    refetch: refetchUsers,
    isLoading,
  } = useFindUsers({
    pageSize: useClientSideSearch
      ? SEARCH_FETCH_SIZE
      : useClientSideFilters
        ? FILTER_FETCH_SIZE
        : pageSize,
    page: useClientSideFilters ? 1 : currentPage,
    // Omit searchTerm while searching so older APIs don't drop accented names;
    // local exact+broad filter applies instead. Deployed API also supports broad match.
    searchTerm: useClientSideSearch
      ? undefined
      : debouncedSearchTerm || undefined,
    userType,
    membershipNames: effectiveMembershipFilter
      ? [effectiveMembershipFilter]
      : undefined,
    stripeStatus: effectiveStripeFilter || undefined,
  });

  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUser({
    onSuccess: () => {
      setShowDeactivateModal(false);
      refetchUsers();
    },
  });

  const transformUserData = (user: User) => {
    const photo = resolveProfessionalPhotoUrl(user);
    return {
      user: {
        ...user,
      },
      imageUrls: {
        IdDocument: user.IdDocument || "",
        ProfessionalPhoto: photo,
        BusinessLogo: user.BusinessLogo || "",
        // Legacy key kept for older edit UIs; same canonical blob URL.
        PersonalProfessionalPhoto: photo,
      },
    };
  };

  const mergedUsers = useMemo(() => {
    return (userResponse?.users.data || []).map((user) => {
      const imageEntry = userResponse?.userImageUrls?.find(
        (img) => img.userId === user.id,
      );

      return {
        ...user,
        ...imageEntry?.imageUrls,
      };
    });
  }, [userResponse]);

  const filteredUsers = useMemo(() => {
    return mergedUsers.filter((user) => {
      if (searchTrimmed && !matchesAdminUserSearch(user, searchTrimmed)) {
        return false;
      }
      if (
        effectiveMembershipFilter &&
        !matchesMembershipFilter(user.membershipName, effectiveMembershipFilter)
      ) {
        return false;
      }
      if (
        effectiveStripeFilter &&
        !matchesStripeStatusFilter(user.stripeStatus, effectiveStripeFilter)
      ) {
        return false;
      }
      return true;
    });
  }, [
    mergedUsers,
    searchTrimmed,
    effectiveMembershipFilter,
    effectiveStripeFilter,
  ]);

  const displayedUsers = useMemo(() => {
    if (!useClientSideFilters) return filteredUsers;
    const start = (currentPage - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, useClientSideFilters, currentPage, pageSize]);

  const totalPages = useClientSideFilters
    ? Math.max(1, Math.ceil(filteredUsers.length / pageSize))
    : userResponse?.users.totalPages || 1;

  const navigate = useNavigate();
  const openUserProfile = (objectId?: string) => {
    if (!objectId) return;
    const from = `${location.pathname}${location.search}${location.hash}`;
    navigate(`/user-profile/${objectId}`, {
      state: {
        from,
        adminTab: "users",
        usersSubTab: userType,
      },
    });
  };

  const handleEdit = (user: User) => {
    if (!user.isPortalUser) {
      return;
    }
    if (user) {
      setShowEditModal(true);
      setUserData(user);
    }
  };

  const handleDeactivate = () => {
    if (userData?.isPortalUser && userData?.id !== undefined) {
      deleteUser(userData.id);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (!useClientSideFilters) {
      refetchUsers();
    }
  };

  const canManageSelectedUser = !!userData?.isPortalUser;

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="w-100">
      <AdminPanelHeader
        icon={<FiUsers size={22} />}
        title="User Management"
        description="Review and manage portal users."
      />
      <Tabs
        id="admin-users-subtabs"
        activeKey={userType}
        onSelect={(k) => {
          const nextType = (k as "all" | "members" | "explorer") || "all";
          setUserType(nextType);
          setCurrentPage(1);
        }}
        className="mb-3"
      >
        <Tab eventKey="all" title="All Users" />
        <Tab eventKey="members" title="Members" />
        <Tab eventKey="explorer" title="Explorer" />
      </Tabs>

      <div className="card border shadow-sm rounded-3 mb-4">
        <div className="card-body py-3">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={showMemberFilters ? 4 : 12}>
              <Form.Label htmlFor="admin-users-search" className="small fw-semibold mb-1">
                Search
              </Form.Label>
              <Form.Control
                id="admin-users-search"
                type="search"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            {showMemberFilters ? (
              <>
                <Col xs={12} md={4}>
                  <Form.Label
                    htmlFor="admin-users-membership-filter"
                    className="small fw-semibold mb-1"
                  >
                    Membership type
                  </Form.Label>
                  <Form.Select
                    id="admin-users-membership-filter"
                    value={membershipFilter}
                    onChange={(e) => setMembershipFilter(e.target.value)}
                    aria-label="Filter by membership type"
                  >
                    <option value="">All memberships</option>
                    {membershipOptions.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Label
                    htmlFor="admin-users-stripe-filter"
                    className="small fw-semibold mb-1"
                  >
                    Stripe status
                  </Form.Label>
                  <Form.Select
                    id="admin-users-stripe-filter"
                    value={stripeStatusFilter}
                    onChange={(e) => setStripeStatusFilter(e.target.value)}
                    aria-label="Filter by Stripe status"
                  >
                    <option value="">All statuses</option>
                    {STRIPE_STATUS_FILTERS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </>
            ) : null}
          </Row>
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex p-4 justify-content-center">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div className="admin-data-table-wrap bg-white overflow-hidden">
          <Table
            hover
            responsive
            className="admin-data-table align-middle mb-0"
          >
            <thead>
              <tr>
                <th className="fw-semibold text-uppercase text-nowrap align-middle">
                  User
                </th>
                <th className="fw-semibold text-uppercase text-nowrap align-middle">
                  Email
                </th>
                <th className="fw-semibold text-uppercase text-nowrap align-middle">
                  Location
                </th>
                <th className="fw-semibold text-uppercase text-nowrap align-middle">
                  Category
                </th>
                <th className="fw-semibold text-uppercase text-nowrap align-middle">
                  Verified
                </th>
                {showMemberFilters ? (
                  <>
                    <th className="fw-semibold text-uppercase text-nowrap align-middle">
                      Membership
                    </th>
                    <th className="fw-semibold text-uppercase text-nowrap align-middle">
                      Stripe
                    </th>
                  </>
                ) : null}
                <th className="fw-semibold text-uppercase text-nowrap align-middle text-end">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={showMemberFilters ? 8 : 6}
                    className="text-center text-muted py-4"
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                displayedUsers.map((conn) => {
                  const fullName =
                    `${conn.firstName || ""} ${conn.lastName || ""}`.trim() ||
                    "—";
                  const locationLabel = formatCityCountryLabel(
                    conn.city,
                    conn.country,
                  );
                  const category = (conn.userCategory || "").trim();
                  const membership = (conn.membershipName || "").trim();
                  const stripeStatus = (conn.stripeStatus || "").trim();
                  const isEmailVerified = conn.isEmailVerified === true;
                  const photo = resolveProfessionalPhotoUrl(conn, "/user.png");

                  return (
                    <tr key={conn.objectId || String(conn.id) || conn.email}>
                      <td className="align-middle" style={{ maxWidth: 220 }}>
                        <button
                          type="button"
                          className="btn btn-link events-table-name-link p-0 border-0 shadow-none text-decoration-none d-flex align-items-center gap-2 text-start w-100 overflow-hidden"
                          onClick={() => openUserProfile(conn.objectId)}
                          title={fullName}
                        >
                          <img
                            src={photo}
                            className="rounded-circle object-fit-cover flex-shrink-0"
                            width={40}
                            height={40}
                            alt=""
                          />
                          <span className="fw-semibold text-truncate lh-sm min-w-0">
                            {fullName}
                          </span>
                        </button>
                      </td>
                      <td className="admin-table-muted align-middle" style={{ maxWidth: 240 }}>
                        <span
                          className="text-truncate d-block"
                          title={conn.email || undefined}
                        >
                          {conn.email || "—"}
                        </span>
                      </td>
                      <td className="admin-table-muted align-middle">
                        {locationLabel || "—"}
                      </td>
                      <td className="align-middle">
                        {category ? (
                          <Badge
                            bg="primary-subtle"
                            text="primary-emphasis"
                            pill
                            className="fw-semibold"
                          >
                            {category}
                          </Badge>
                        ) : (
                          <span className="admin-table-muted">—</span>
                        )}
                      </td>
                      <td className="align-middle">
                        {isEmailVerified ? (
                          <Badge
                            bg="success-subtle"
                            text="success-emphasis"
                            pill
                            className="fw-semibold"
                          >
                            Verified
                          </Badge>
                        ) : (
                          <Badge
                            bg="warning-subtle"
                            text="warning-emphasis"
                            pill
                            className="fw-semibold"
                          >
                            Not verified
                          </Badge>
                        )}
                      </td>
                      {showMemberFilters ? (
                        <>
                          <td className="align-middle">
                            {membership ? (
                              <Badge
                                bg="light"
                                text="dark"
                                pill
                                className="fw-semibold border"
                              >
                                {membership}
                              </Badge>
                            ) : (
                              <span className="admin-table-muted">—</span>
                            )}
                          </td>
                          <td className="align-middle">
                            {stripeStatus ? (
                              <Badge
                                bg={`${stripeStatusBadgeTone(stripeStatus)}-subtle`}
                                text={`${stripeStatusBadgeTone(stripeStatus)}-emphasis`}
                                pill
                                className="fw-semibold text-capitalize"
                              >
                                {stripeStatus.replace(/_/g, " ")}
                              </Badge>
                            ) : (
                              <span className="admin-table-muted">—</span>
                            )}
                          </td>
                        </>
                      ) : null}
                      <td className="align-middle text-end text-nowrap">
                        <Button
                          size="sm"
                          variant="link"
                          disabled={!conn.isPortalUser}
                          onClick={() => handleEdit(conn)}
                          className="admin-table-action-btn p-0 d-inline-flex align-items-center justify-content-center text-decoration-none"
                          aria-label="Edit"
                        >
                          <FaEdit size={15} />
                        </Button>
                        <Button
                          size="sm"
                          variant="link"
                          disabled={!conn.isPortalUser}
                          onClick={() => {
                            setShowDeactivateModal(true);
                            setUserData(conn);
                          }}
                          className="admin-table-action-btn admin-table-action-btn--danger p-0 d-inline-flex align-items-center justify-content-center text-decoration-none"
                          aria-label="Deactivate"
                        >
                          <BsTrash size={15} />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      )}

      {!isLoading && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {pageNumbers.map((pageNum) => (
              <Pagination.Item
                key={pageNum}
                active={pageNum === currentPage}
                onClick={() => handlePageChange(pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      <EditModal
        showEditModal={showEditModal}
        setShowEditModal={() => setShowEditModal(false)}
        userData={userData ? transformUserData(userData) : null}
      />
      <Modal
        centered
        show={showDeactivateModal}
        onHide={() => setShowDeactivateModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Deactivate Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {canManageSelectedUser
            ? "Are you sure you want to deactivate your account? This action cannot be undone."
            : "This user only has auth/explorer record and cannot be deactivated from portal users table."}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeactivateModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeactivate}
            disabled={isDeleting || !canManageSelectedUser}
          >
            {isDeleting ? "Deactivating..." : "Deactivate"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageUsers;
